import React, { useEffect } from "react";
import { Dialog } from "@mui/material";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";
import { sendEmail } from "../../../NetworkServices/services";
import { validateContactForm } from "../../../utils/inputSanitizer";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const LOAN_TYPES = [
  "Business Loans",
  "Personal Loans",
  "Z-ForHer Credit Line",
  "School Fees Loan",
  "Other",
];

const ONBOARDING_URL = "https://zikoramfb.qoreonline.com/onboarding/type";

const labelStyle = {
  ...raleway,
  fontWeight: 500,
  fontSize: "13px",
  color: "#132A22",
  marginBottom: "8px",
  display: "block",
};

const baseFieldStyle = {
  ...raleway,
  width: "100%",
  height: "48px",
  padding: "0 16px",
  background: "#FFFFFF",
  border: "1px solid #E5E9E6",
  borderRadius: "10px",
  fontSize: "14px",
  color: "#132A22",
  outline: "none",
  boxSizing: "border-box",
};

const errorBorder = { borderColor: "#D14343" };

const LoanApplyModal = ({
  open,
  onClose,
  defaultLoanType = "",
  defaultAmount = "",
  contextNote = "",
  isLoading,
  setIsLoading,
}) => {
  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      phone: "",
      loanType: defaultLoanType,
      address: "",
      amount: String(defaultAmount),
      purpose: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};

      const validation = validateContactForm({
        name: values?.fullName,
        email: values?.email,
        body: "placeholder body to satisfy contact validator",
      });
      if (validation?.errors?.name) errors.fullName = validation?.errors?.name;
      if (validation?.errors?.email) errors.email = validation?.errors?.email;

      if (!values?.phone?.trim()) {
        errors.phone = "Phone number is required.";
      } else if (!/^[+\d][\d\s-]{6,}$/.test(values?.phone?.trim())) {
        errors.phone = "Enter a valid phone number.";
      }

      if (!values?.loanType) errors.loanType = "Please pick a loan type.";
      if (!values?.address?.trim()) errors.address = "Address is required.";
      const amount = Number(String(values?.amount || "").replace(/[₦,\s]/g, ""));
      if (!Number.isFinite(amount) || amount <= 0) errors.amount = "Enter a valid loan amount.";
      if (!values?.purpose?.trim()) errors.purpose = "Loan purpose is required.";

      return errors;
    },
    onSubmit: (values) => {
      setIsLoading(true);

      const name = values?.fullName?.trim();
      const email = values?.email?.trim();
      const phone = values?.phone?.trim();
      const loanType = values?.loanType;
      const address = values?.address?.trim();
      const amount = values?.amount?.trim();
      const purpose = values?.purpose?.trim();

      const data = {
        userEmail: email,
        zikoraEmail: "customer.service@zikoramfb.com",
        subject: `Loan Application — ${loanType} — ${name}`,
        body: `New loan application enquiry\n\nName: ${name}\nEmail: ${email}\nPhone: ${phone}\nLoan Type: ${loanType}\nAddress: ${address}\nLoan Amount: ${amount}\nLoan Purpose: ${purpose}${
          contextNote ? `\n\n${contextNote}` : ""
        }\n\nA real person from the Zikora team will reach out with next steps.`,
      };

      sendEmail(data)
        .then((val) => {
          setIsLoading(false);
          if (val?.status === 200) {
            toast.success(
              val?.data?.message ||
                "Thanks — your details are in. A Zikora banker will reach out shortly."
            );
            formik.resetForm();
            onClose();
          } else {
            toast.error(
              val?.data?.message ||
                "Couldn't submit your details. Please try again."
            );
          }
        })
        .catch(() => {
          setIsLoading(false);
          toast.error("Couldn't submit your details. Please try again.");
        });
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const errFor = (n) =>
    formik?.touched?.[n] && formik?.errors?.[n] ? formik?.errors?.[n] : null;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          padding: { xs: "20px", sm: "32px" },
          background: "#FFFFFF",
          boxShadow: "0 24px 64px rgba(19, 42, 34, 0.18)",
          maxHeight: "calc(100dvh - 32px)",
          overflowY: "auto",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#295B4A",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  ...geistMono,
                  fontWeight: 500,
                  fontSize: "11px",
                  lineHeight: 1,
                  letterSpacing: "0.1em",
                  color: "#295B4A",
                  textTransform: "uppercase",
                }}
              >
                Submit details
              </span>
            </div>

            <h3
              style={{
                ...instrumentSerif,
                fontWeight: 400,
                fontSize: "clamp(22px, 2.6vw, 28px)",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                color: "#132A22",
                margin: 0,
              }}
            >
              Apply for a Zikora loan
            </h3>

            <p
              style={{
                ...raleway,
                fontWeight: 400,
                fontSize: "13px",
                lineHeight: 1.55,
                color: "#6B7B70",
                margin: 0,
              }}
            >
              Share a few quick details and a Zikora banker will reach out to
              walk you through the next steps.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "#F1F4F0",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#295B4A",
              cursor: isLoading ? "default" : "pointer",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={formik.handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="loan-apply-fullName">
              Full Name
            </label>
            <input
              id="loan-apply-fullName"
              name="fullName"
              type="text"
              placeholder="e.g Amanda Eze"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                ...baseFieldStyle,
                ...(errFor("fullName") && errorBorder),
              }}
            />
            {errFor("fullName") && (
              <span
                style={{
                  ...raleway,
                  fontSize: "12px",
                  color: "#D14343",
                  marginTop: "6px",
                  display: "block",
                }}
              >
                {errFor("fullName")}
              </span>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-email">
              Email Address
            </label>
            <input
              id="loan-apply-email"
              name="email"
              type="email"
              placeholder="you@email.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                ...baseFieldStyle,
                ...(errFor("email") && errorBorder),
              }}
            />
            {errFor("email") && (
              <span
                style={{
                  ...raleway,
                  fontSize: "12px",
                  color: "#D14343",
                  marginTop: "6px",
                  display: "block",
                }}
              >
                {errFor("email")}
              </span>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-phone">
              Phone Number
            </label>
            <input
              id="loan-apply-phone"
              name="phone"
              type="tel"
              placeholder="+234..."
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                ...baseFieldStyle,
                ...(errFor("phone") && errorBorder),
              }}
            />
            {errFor("phone") && (
              <span
                style={{
                  ...raleway,
                  fontSize: "12px",
                  color: "#D14343",
                  marginTop: "6px",
                  display: "block",
                }}
              >
                {errFor("phone")}
              </span>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-type">
              Loan Type
            </label>
            <div style={{ position: "relative" }}>
              <select
                id="loan-apply-type"
                name="loanType"
                value={formik.values.loanType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  ...baseFieldStyle,
                  ...(errFor("loanType") && errorBorder),
                  appearance: "none",
                  WebkitAppearance: "none",
                  cursor: "pointer",
                  paddingRight: "44px",
                  color: formik.values.loanType ? "#132A22" : "#9CA3AF",
                }}
              >
                <option value="" disabled>
                  Pick a loan type
                </option>
                {LOAN_TYPES?.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <MdKeyboardArrowDown
                size={20}
                style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#9CA3AF",
                }}
              />
            </div>
            {errFor("loanType") && (
              <span
                style={{
                  ...raleway,
                  fontSize: "12px",
                  color: "#D14343",
                  marginTop: "6px",
                  display: "block",
                }}
              >
                {errFor("loanType")}
              </span>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-address">Address</label>
            <input
              id="loan-apply-address"
              name="address"
              type="text"
              autoComplete="street-address"
              placeholder="Enter your address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={Boolean(errFor("address"))}
              style={{ ...baseFieldStyle, ...(errFor("address") && errorBorder) }}
            />
            {errFor("address") && <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>{errFor("address")}</span>}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-amount">Loan Amount</label>
            <input
              id="loan-apply-amount"
              name="amount"
              type="text"
              inputMode="decimal"
              placeholder="Enter amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={Boolean(errFor("amount"))}
              style={{ ...baseFieldStyle, ...(errFor("amount") && errorBorder) }}
            />
            {errFor("amount") && <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>{errFor("amount")}</span>}
          </div>

          <div>
            <label style={labelStyle} htmlFor="loan-apply-purpose">Loan Purpose</label>
            <textarea
              id="loan-apply-purpose"
              name="purpose"
              rows={3}
              placeholder="Tell us what the loan is for"
              value={formik.values.purpose}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={Boolean(errFor("purpose"))}
              style={{ ...baseFieldStyle, height: "auto", minHeight: "88px", padding: "12px 16px", resize: "vertical", ...(errFor("purpose") && errorBorder) }}
            />
            {errFor("purpose") && <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>{errFor("purpose")}</span>}
          </div>

          {/* Action row */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "4px",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...raleway,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: "48px",
                width: "100%",
                padding: "0 22px",
                background: isLoading ? "#B8D4C5" : "#86C7A8",
                border: "none",
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "14px",
                color: "#132A22",
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              {isLoading ? (
                <CircularProgress size={18} sx={{ color: "#132A22" }} />
              ) : (
                "Submit Details"
              )}
            </button>

            <a
              href={ONBOARDING_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...raleway,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                height: "44px",
                width: "100%",
                padding: "0 18px",
                background: "transparent",
                border: "1px solid #E5E9E6",
                borderRadius: "10px",
                fontWeight: 500,
                fontSize: "13px",
                color: "#132A22",
                textDecoration: "none",
              }}
            >
              Or onboard directly with Qore →
            </a>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default LoanApplyModal;
